// CameraProvider.tsx
import type React from "react";
import {createContext, useCallback, useRef} from "react";

import * as THREE from "three";

import type CameraContextType from "./CameraContext";

export const CameraContext = createContext<CameraContextType | undefined>(
  undefined,
);

export const CameraProvider: React.FC<{children: React.ReactNode}> = ({
  children,
}) => {
  // Target state that you want to head toward.
  const targetPosition = useRef(new THREE.Vector2(0, 0));
  const targetZoom = useRef(100);

  // Current state used in smoothing & applied directly to the camera.
  const currentPosition = useRef(new THREE.Vector2(0, 0));
  const currentZoom = useRef(100);

  // Velocities for the smooth motion.
  const cameraVelocity = useRef(new THREE.Vector2(0, 0));
  const zoomVelocity = useRef(0);

  // When setting the current position/zoom, we want to immediately update the state
  // and reset the velocity so that the smooth animation starts fresh.
  const setCurrentPosition = useCallback((position: THREE.Vector2) => {
    currentPosition.current.copy(position);
    cameraVelocity.current.set(0, 0);
  }, []);

  const setCurrentZoom = useCallback((zoom: number) => {
    currentZoom.current = zoom;
    zoomVelocity.current = 0;
  }, []);

  return (
    <CameraContext.Provider
      value={{
        targetPosition,
        targetZoom,
        currentPosition,
        currentZoom,
        cameraVelocity,
        zoomVelocity,
        setCurrentPosition,
        setCurrentZoom,
      }}
    >
      {children}
    </CameraContext.Provider>
  );
};

export default CameraProvider;
